// ============================================
// Stable per-device identifier for attempt buckets
// ============================================

/**
 * localStorage key holding this browser's device id. Deliberately its own
 * top-level key (NOT inside the per-level progress maps) so it is never
 * synced to the cloud as a record — it only ever appears as a key inside
 * `WordProgress.attempts` maps.
 */
const DEVICE_ID_STORAGE_KEY = 'jlpt-device-id';

/**
 * Reserved bucket id for pre-migration flat counters folded in by
 * `normalizeAttempts`. Cannot collide with a real device id — real ids are
 * `crypto.randomUUID()` strings.
 */
export const LEGACY_DEVICE_ID = 'legacy';

/**
 * Reserved bucket id used when localStorage is unavailable (private mode).
 * All such sessions share one bucket, so their merges degrade to the old
 * max() undercount — bounded, and far better than unbounded key growth from
 * minting a fresh UUID per session that can never persist.
 */
export const EPHEMERAL_DEVICE_ID = 'ephemeral';

// Module-level cache: resolve the id once per session. Cached even when the
// resolution degraded to "ephemeral" — the spec accepts ephemeral degradation
// for the whole session, and re-probing a throwing localStorage on every
// grade would just add noise to the hot path.
let cachedDeviceId: string | null = null;

/**
 * The stable id for this device, created on first use and persisted at
 * `jlpt-device-id`. Falls back to the shared `"ephemeral"` id when storage
 * throws (private browsing / disabled localStorage) — grading must never
 * fail because an id couldn't persist.
 */
export function getDeviceId(): string {
  if (cachedDeviceId !== null) return cachedDeviceId;
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) {
      cachedDeviceId = existing;
    } else {
      const id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
      cachedDeviceId = id;
    }
  } catch {
    cachedDeviceId = EPHEMERAL_DEVICE_ID;
  }
  return cachedDeviceId;
}

/**
 * Test hook: pin the device id to a known value, or pass `null` to clear the
 * cache so the next `getDeviceId()` re-resolves from storage. Wired into the
 * global test-setup `beforeEach` alongside `resetAppState()`.
 */
export function __setDeviceIdForTests(id: string | null): void {
  cachedDeviceId = id;
}
